import React, { ReactNode, useCallback, useContext, useState } from 'react';
import classNames from 'classnames';
import { useTranslation } from 'react-i18next';
import DashCardHeading from '../DashCardHeading';
import FireIcon from 'icons/Fire.icon';
import LeadInfo from '../LeadInfo';
import { Loader } from 'ncoded-component-library';
import InfiniteScroll from 'components/InfiniteScroll';
import DataRoomContext from 'router/subrouters/DataRoom/providers/DataRoom/DataRoom.context';
import { HOT_LEAD_THRESHOLD } from 'router/subrouters/DataRoom/models/Dashboard';
import useHotLeadsInfiniteQuery from '../../hooks/useHotLeadsInfiniteQuery';
import { getIsArrayEmpty, getIsSortApplied } from 'utils';
import { IconProp } from 'models/Icon';
import EyeIcon from 'icons/Eye.icon';
import AskQuestionIcon from 'icons/AskQuestion.icon';
import DownloadIcon from 'icons/Download.icon';
import { SortingButton } from 'router/subrouters/DataRoom/components/SortingButton';
import { ObjectValues } from 'types';
import useMatchMedia from 'hooks/useMatchMedia';

import './HotLeads.styles.scss';

const SORT_NAME = {
  NAME: 'name',
  VIEW_COUNT: 'viewCount',
  QUESTION_COUNT: 'questionCount',
  DOWNLOAD_COUNT: 'downloadCount',
} as const;

type SortName = ObjectValues<typeof SORT_NAME>;

const SORT_LABEL_COPY: Record<SortName, string> = {
  [SORT_NAME.NAME]: 'name',
  [SORT_NAME.VIEW_COUNT]: 'views',
  [SORT_NAME.QUESTION_COUNT]: 'questions',
  [SORT_NAME.DOWNLOAD_COUNT]: 'downloads',
};

const SORT_ICON: Partial<Record<SortName, IconProp>> = {
  [SORT_NAME.VIEW_COUNT]: EyeIcon,
  [SORT_NAME.QUESTION_COUNT]: AskQuestionIcon,
  [SORT_NAME.DOWNLOAD_COUNT]: DownloadIcon,
};

type HotLeadsProps = {
  className?: string;
};

const HotLeads: React.FC<HotLeadsProps> = (props) => {
  const { className } = props;

  const classes = classNames('hot-leads', 'white-panel', className);

  const [sort, setSort] = useState<string>(`-${SORT_NAME.VIEW_COUNT}`);

  const { t } = useTranslation();
  const {
    dataRoom: { _id: dataRoomId },
    preferredDashboardTimeframe,
  } = useContext(DataRoomContext);

  const { query, items, totalItems } = useHotLeadsInfiniteQuery({
    dataRoomId,
    timeframe: preferredDashboardTimeframe,
    sort,
  });

  const {
    isLoading,
    isPreviousData,
    isFetchingNextPage,
    hasNextPage,
    fetchNextPage,
  } = query;

  const isPhablet = useMatchMedia('isPhablet');

  const hasNoLeads = getIsArrayEmpty(items);

  const handleScroll = useCallback(() => {
    if (!hasNextPage || isFetchingNextPage) return;

    fetchNextPage();
  }, [fetchNextPage, hasNextPage, isFetchingNextPage]);

  const renderAction = useCallback(
    (count: number, Icon: IconProp, sortName: SortName) => (
      <div
        className={classNames('action-count', {
          selected: getIsSortApplied(sort, sortName),
        })}
      >
        {count}
        <Icon />
      </div>
    ),
    [sort],
  );

  const renderSortButton = useCallback(
    (sortName: SortName, content?: ReactNode) => {
      const Icon = SORT_ICON[sortName];

      return (
        <SortingButton
          content={isPhablet && Icon ? <Icon /> : content}
          sortingValue={sortName}
          sort={sort}
          updateSort={setSort}
        />
      );
    },
    [sort, isPhablet],
  );

  return (
    <div className={classes}>
      <DashCardHeading
        heading={t('hotLeads')}
        description={t('hotLeadsDescription', { count: HOT_LEAD_THRESHOLD })}
        icon={FireIcon}
        numberOfElements={totalItems}
      />

      {isLoading && hasNoLeads ? (
        <Loader overlay={false} />
      ) : hasNoLeads ? (
        <p className="dashboard__empty">{t('noHotLeadsDescription')}</p>
      ) : (
        <InfiniteScroll
          localScroll
          className="dashboard__scroll"
          onScroll={handleScroll}
        >
          <div className="dashboard__list">
            <div
              className={classNames(
                'dashboard__list__header',
                'hot-leads__list-header',
              )}
            >
              {Object.values(SORT_NAME).map((sortName) =>
                renderSortButton(sortName, t(SORT_LABEL_COPY[sortName])),
              )}
            </div>

            {items.map(
              ({
                dataRoomMemberId,
                firstName,
                lastName,
                lastSeen,
                email,
                avatarColor,
                lastSessionDurationSeconds,
                viewCount,
                downloadCount,
                questionCount,
              }) => (
                <div key={dataRoomMemberId} className="dashboard__list__item">
                  <LeadInfo
                    avatarColor={avatarColor}
                    firstName={firstName}
                    lastName={lastName}
                    email={email}
                    lastSeen={lastSeen}
                    lastSession={lastSessionDurationSeconds}
                  />

                  <div className="hot-leads__list__item__actions">
                    {renderAction(viewCount, EyeIcon, SORT_NAME.VIEW_COUNT)}
                    {renderAction(
                      questionCount,
                      AskQuestionIcon,
                      SORT_NAME.QUESTION_COUNT,
                    )}
                    {renderAction(
                      downloadCount,
                      DownloadIcon,
                      SORT_NAME.DOWNLOAD_COUNT,
                    )}
                  </div>
                </div>
              ),
            )}
          </div>
        </InfiniteScroll>
      )}

      {(isFetchingNextPage || (isPreviousData && !hasNoLeads)) && (
        <Loader overlay={false} />
      )}
    </div>
  );
};

export default HotLeads;
